(function () {
  if (window.GardenIrl) return;

  const instances = new WeakMap();

  function pricingTiers(value) {
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch (_) { return []; }
    }
    if (Array.isArray(value)) return value;
    return value && typeof value === 'object' ? [value] : [];
  }

  function livePricingTier(tier, today) {
    const validDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    if (tier.startsAt && !validDate(tier.startsAt)) return false;
    if (tier.endsAt && !validDate(tier.endsAt)) return false;
    if (tier.startsAt && tier.endsAt && tier.startsAt > tier.endsAt) return false;
    return (!tier.startsAt || tier.startsAt <= today) && (!tier.endsAt || tier.endsAt >= today);
  }

  function updateProductPrices(section) {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
    section.querySelectorAll('[data-garden-irl-price]').forEach((root) => {
      const price = root.querySelector('.price');
      const configElement = root.querySelector('[data-garden-irl-price-config]');
      const basePrice = Number(root.dataset.basePrice);
      if (!price || !configElement || !Number.isFinite(basePrice)) return;

      let config;
      try { config = JSON.parse(configElement.textContent); } catch (_) { return; }
      if (!config || typeof config !== 'object') return;

      const bulkTier = pricingTiers(config.bulkPricingTiers)
        .filter((tier) => Number.isFinite(Number(tier?.minQty))
          && Number(tier.minQty) >= 1
          && Number(tier.minQty) <= 1
          && Number.isFinite(Number(tier.discount))
          && Number(tier.discount) > 0
          && livePricingTier(tier, today))
        .sort((a, b) => Number(b.minQty) - Number(a.minQty))[0];
      const bulkPercent = bulkTier ? Number(bulkTier.discount) : 0;
      const isMember = price.classList.contains('price--member');
      let displayPrice = null;

      if (isMember) {
        const overrideSource = config.variantOverride == null ? config.productOverride : config.variantOverride;
        const override = pricingTiers(overrideSource)
          .filter((tier) => ['amount', 'percentage'].includes(String(tier?.type || '').toLowerCase())
            && Number.isFinite(Number(tier.value))
            && Number(tier.value) > 0
            && Number.isFinite(Number(tier.minQty ?? 1))
            && Number(tier.minQty ?? 1) >= 1
            && Number(tier.minQty ?? 1) <= 1
            && livePricingTier(tier, today))
          .sort((a, b) => Number(b.minQty ?? 1) - Number(a.minQty ?? 1))[0];
        if (override) {
          const value = Number(override.value);
          displayPrice = String(override.type).toLowerCase() === 'percentage'
            ? Math.round(basePrice * (1 - Math.min(value, 100) / 100))
            : Math.round(Math.max(0, basePrice - value * 100));
        } else if (bulkPercent > 0) {
          displayPrice = Math.max(0, basePrice - Math.round(basePrice * Math.min(bulkPercent + 10, 100) / 100));
        }
      } else if (bulkPercent > 0) {
        displayPrice = Math.max(0, basePrice - Math.round(basePrice * Math.min(bulkPercent, 100) / 100));
      } else if (root.dataset.nativeVolumePricing === 'true') {
        displayPrice = basePrice;
      }

      if (displayPrice === null) return;
      const amount = isMember
        ? price.querySelector('.price__member-display .price-item--sale')
        : price.querySelector('.price__sale .price-item--sale, .price__regular .price-item--regular');
      if (!amount) return;
      amount.textContent = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        minimumFractionDigits: displayPrice % 100 === 0 ? 0 : 2,
        maximumFractionDigits: displayPrice % 100 === 0 ? 0 : 2,
      }).format(displayPrice / 100);
    });
  }

  function init(section) {
    if (instances.has(section)) return;

    const rail = section.querySelector('[data-garden-irl-rail]');
    const dialog = section.querySelector('[data-garden-irl-dialog]');
    const stage = section.querySelector('[data-garden-irl-stage]');
    if (!rail || !dialog || !stage) return;

    const controller = new AbortController();
    const { signal } = controller;
    const cards = Array.from(rail.querySelectorAll('.garden-irl__card'));
    const videos = cards.map((card) => card.querySelector('video')).filter(Boolean);
    const prev = section.querySelector('[data-garden-irl-prev]');
    const next = section.querySelector('[data-garden-irl-next]');
    const headerActions = section.querySelector('.garden-irl__header-actions');
    const dots = Array.from(section.querySelectorAll('[data-garden-irl-dot]'));
    const closeButton = section.querySelector('[data-garden-irl-close]');
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const saveData = Boolean(navigator.connection?.saveData);
    const hoverTimers = new Map();
    let modalTrigger = null;

    updateProductPrices(section);

    function closeProductTrays(except) {
      cards.forEach((card) => {
        const toggle = card.querySelector('[data-garden-irl-products-toggle]');
        const panel = card.querySelector('[data-garden-irl-products-panel]');
        if (!toggle || !panel || toggle === except) return;
        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
      });
    }

    function updateNavigation() {
      const canScroll = rail.scrollWidth > rail.clientWidth + 2;
      if (headerActions) headerActions.hidden = !canScroll;
      if (prev && next) {
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      }
      if (dots.length) {
        const railLeft = rail.getBoundingClientRect().left;
        const activeIndex = cards.reduce((closest, card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - railLeft);
          return distance < closest.distance ? { index, distance } : closest;
        }, { index: 0, distance: Infinity }).index;
        dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === activeIndex)));
      }
    }

    function move(direction) {
      if (!cards.length) return;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      rail.scrollBy({
        left: direction * (cards[0].getBoundingClientRect().width + gap),
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    function loadVideo(video) {
      const source = video.querySelector('source[data-src]');
      if (!source) return;
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
      video.load();
    }

    function syncCard(card) {
      const video = card.querySelector('video');
      const media = card.querySelector('[data-garden-irl-media]');
      const play = card.querySelector('[data-garden-irl-play]');
      const pause = card.querySelector('[data-garden-irl-pause]');
      const mute = card.querySelector('[data-garden-irl-mute]');
      const expand = card.querySelector('[data-garden-irl-fullscreen]');
      if (!video || !media) return;

      const playing = !video.paused && !video.ended;
      media.classList.toggle('is-playing', playing);
      media.classList.toggle('is-previewing', playing);
      if (pause) pause.hidden = !playing;
      if (mute) {
        mute.hidden = !playing;
        mute.classList.toggle('is-unmuted', !video.muted);
        mute.setAttribute('aria-label', `${video.muted ? 'Unmute' : 'Mute'} ${video.getAttribute('aria-label')}`);
      }
      if (expand) expand.hidden = !playing;
      if (!playing && document.activeElement === pause) play?.focus({ preventScroll: true });
    }

    function stopPreview(card, reset = true) {
      clearTimeout(hoverTimers.get(card));
      hoverTimers.delete(card);
      const video = card.querySelector('video');
      if (!video) return;
      video.pause();
      video.muted = true;
      if (reset && video.readyState > 0) video.currentTime = 0;
      syncCard(card);
    }

    function stopOtherVideos(active) {
      cards.forEach((card) => {
        if (card !== active) stopPreview(card);
      });
    }

    function startPreview(card) {
      if (!finePointer.matches || reduceMotion.matches || saveData || dialog.open) return;
      clearTimeout(hoverTimers.get(card));
      hoverTimers.set(card, setTimeout(() => {
        hoverTimers.delete(card);
        const media = card.querySelector('[data-garden-irl-media]');
        const video = card.querySelector('video');
        if (!media?.matches(':hover') || !video) return;
        stopOtherVideos(card);
        loadVideo(video);
        video.muted = true;
        video.loop = true;
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => syncCard(card));
      }, 150));
    }

    function openModal(card) {
      const preview = card.querySelector('video');
      if (!preview || dialog.open || typeof dialog.showModal !== 'function') return;
      loadVideo(preview);
      const source = preview.querySelector('source');
      const src = source?.src;
      if (!src) return;

      stopOtherVideos(null);
      const expandedVideo = document.createElement('video');
      expandedVideo.src = src;
      expandedVideo.poster = preview.poster;
      expandedVideo.controls = true;
      expandedVideo.playsInline = true;
      expandedVideo.preload = 'metadata';
      expandedVideo.setAttribute('aria-label', preview.getAttribute('aria-label'));
      stage.replaceChildren(expandedVideo);
      dialog.setAttribute('aria-label', preview.getAttribute('aria-label'));
      modalTrigger = card.querySelector('[data-garden-irl-play]');
      dialog.showModal();
      closeButton?.focus({ preventScroll: true });
      const playPromise = expandedVideo.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    }

    cards.forEach((card) => {
      const media = card.querySelector('[data-garden-irl-media]');
      const video = card.querySelector('video');
      const play = card.querySelector('[data-garden-irl-play]');
      const pause = card.querySelector('[data-garden-irl-pause]');
      const mute = card.querySelector('[data-garden-irl-mute]');
      const expand = card.querySelector('[data-garden-irl-fullscreen]');
      const productsToggle = card.querySelector('[data-garden-irl-products-toggle]');
      const productsPanel = card.querySelector('[data-garden-irl-products-panel]');
      if (!media || !video || !play) return;

      media.addEventListener('pointerenter', () => startPreview(card), { signal });
      card.addEventListener('pointerleave', () => stopPreview(card), { signal });
      media.addEventListener('click', (event) => {
        if (event.target.closest('button')) return;
        openModal(card);
      }, { signal });
      play.addEventListener('click', () => openModal(card), { signal });
      pause?.addEventListener('click', () => video.pause(), { signal });
      expand?.addEventListener('click', () => openModal(card), { signal });
      productsToggle?.addEventListener('click', () => {
        const isOpen = productsToggle.getAttribute('aria-expanded') === 'true';
        closeProductTrays(isOpen ? null : productsToggle);
        productsToggle.setAttribute('aria-expanded', String(!isOpen));
        if (productsPanel) productsPanel.hidden = isOpen;
      }, { signal });
      productsPanel?.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (productsPanel.hidden || productsPanel.contains(document.activeElement) || productsToggle === document.activeElement) return;
          productsToggle.setAttribute('aria-expanded', 'false');
          productsPanel.hidden = true;
        });
      }, { signal });
      mute?.addEventListener('click', () => {
        video.muted = !video.muted;
        syncCard(card);
      }, { signal });
      video.addEventListener('play', () => syncCard(card), { signal });
      video.addEventListener('pause', () => syncCard(card), { signal });
      video.addEventListener('ended', () => {
        if (finePointer.matches && !reduceMotion.matches && !saveData && !dialog.open && card.matches(':hover')) {
          video.currentTime = 0;
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => syncCard(card));
        } else {
          syncCard(card);
        }
      }, { signal });
    });

    function closeModal() {
      if (dialog.open) dialog.close();
    }

    closeButton?.addEventListener('click', closeModal, { signal });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeModal();
    }, { signal });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeModal();
    }, { signal });
    dialog.addEventListener('close', () => {
      stage.querySelector('video')?.pause();
      stage.replaceChildren();
      modalTrigger?.focus({ preventScroll: true });
      modalTrigger = null;
    }, { signal });
    prev?.addEventListener('click', () => move(-1), { signal });
    next?.addEventListener('click', () => move(1), { signal });
    dots.forEach((dot, index) => dot.addEventListener('click', () => {
      const card = cards[index];
      if (!card) return;
      rail.scrollTo({
        left: rail.scrollLeft + card.getBoundingClientRect().left - rail.getBoundingClientRect().left,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }, { signal }));
    document.addEventListener('pointerdown', (event) => {
      if (event.target.closest('[data-garden-irl-products-toggle], [data-garden-irl-products-panel]')) return;
      closeProductTrays();
    }, { signal });
    document.addEventListener('focusin', (event) => {
      if (event.target.closest('[data-garden-irl-products-toggle], [data-garden-irl-products-panel]')) return;
      closeProductTrays();
    }, { signal });
    rail.addEventListener('scroll', updateNavigation, { passive: true, signal });
    window.addEventListener('resize', updateNavigation, { passive: true, signal });
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(updateNavigation) : null;
    resizeObserver?.observe(rail);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        cards.forEach((card) => stopPreview(card));
        stage.querySelector('video')?.pause();
      }
    }, { signal });
    section.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openToggle = section.querySelector('[data-garden-irl-products-toggle][aria-expanded="true"]');
      if (!openToggle) return;
      closeProductTrays();
      openToggle.focus({ preventScroll: true });
    }, { signal });

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) stopPreview(entry.target);
          });
        }, { threshold: 0.1 })
      : null;

    if (observer) cards.forEach((card) => observer.observe(card));
    instances.set(section, { controller, observer, resizeObserver, videos, dialog, stage, hoverTimers });
    updateNavigation();
  }

  function unload(section) {
    const instance = instances.get(section);
    if (!instance) return;
    instance.hoverTimers.forEach((timer) => clearTimeout(timer));
    instance.videos.forEach((video) => video.pause());
    instance.stage.querySelector('video')?.pause();
    if (instance.dialog.open) instance.dialog.close();
    instance.observer?.disconnect();
    instance.resizeObserver?.disconnect();
    instance.controller.abort();
    instances.delete(section);
  }

  function initWithin(root) {
    if (root.matches?.('[data-garden-irl]')) init(root);
    root.querySelectorAll?.('[data-garden-irl]').forEach(init);
  }

  window.GardenIrl = { initWithin, unload };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-garden-irl]')) unload(event.target);
    event.target.querySelectorAll?.('[data-garden-irl]').forEach(unload);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.closest?.('.garden-irl__card');
    if (!card) return;
    const section = card.closest('[data-garden-irl]');
    if (!section) return;
    const instance = instances.get(section);
    if (!instance) init(section);
    card.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const card = event.target.closest?.('.garden-irl__card');
    card?.querySelector('video')?.pause();
  });
})();
